import { ClerkMetadataKey } from '@this+that/utils';
import { sanitizeHtml } from '@this+that/utils/sanitizeHtml';

import {
  convertPlainTextToHtml,
  escapeHtmlText,
  hasTemplateContent,
  isContentEmpty,
  replaceUserInputContent,
  splitEditorContent,
} from './editorContentUtils';
import {
  extractSignatureBlock,
  hasSignatureBlock,
  stripSignatureBlock,
} from './emailSignatureBlock';
import {
  ATTRIBUTE_VALUES,
  DATA_ATTRIBUTES,
} from '../hooks/richTextArea/constants';

export { extractSignatureBlock, stripSignatureBlock };

export const EMAIL_SIGNATURE_MAX_HTML_LENGTH = 2000;

const EMPTY_PARAGRAPH = '<p></p>';
const USER_INPUT_CLOSING_TAG = '</div>';

export interface EmailSignatureConfig {
  enabled: boolean;
  html: string;
}

export const DISABLED_EMAIL_SIGNATURE: EmailSignatureConfig = {
  enabled: false,
  html: '',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const readStoredEmailSignature = (
  unsafeMetadata: Record<string, unknown> | null | undefined,
): EmailSignatureConfig | null => {
  const stored = unsafeMetadata?.[ClerkMetadataKey.EMAIL_SIGNATURE];
  if (
    isRecord(stored) &&
    typeof stored.enabled === 'boolean' &&
    typeof stored.html === 'string'
  ) {
    return { enabled: stored.enabled, html: stored.html };
  }
  return null;
};

export const resolveEmailSignatureConfig = (
  unsafeMetadata: Record<string, unknown> | null | undefined,
): EmailSignatureConfig =>
  readStoredEmailSignature(unsafeMetadata) ?? DISABLED_EMAIL_SIGNATURE;

export interface EmailSignatureSettings extends EmailSignatureConfig {
  accounts?: Record<string, EmailSignatureConfig>;
}

export const EMAIL_SIGNATURE_SETTINGS_MAX_BYTES = 6000;

export const normalizeSignatureAccountKey = (address: string): string =>
  address.trim().toLowerCase();

const parseSignatureConfig = (value: unknown): EmailSignatureConfig | null =>
  isRecord(value) &&
  typeof value.enabled === 'boolean' &&
  typeof value.html === 'string'
    ? { enabled: value.enabled, html: value.html }
    : null;

const parseSignatureAccounts = (
  value: unknown,
): Record<string, EmailSignatureConfig> | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const accounts: Record<string, EmailSignatureConfig> = {};
  Object.entries(value).forEach(([address, entry]) => {
    const config = parseSignatureConfig(entry);
    const key = normalizeSignatureAccountKey(address);
    if (config && key) {
      accounts[key] = config;
    }
  });
  return Object.keys(accounts).length > 0 ? accounts : undefined;
};

export const readStoredEmailSignatureSettings = (
  unsafeMetadata: Record<string, unknown> | null | undefined,
): EmailSignatureSettings | null => {
  const stored = unsafeMetadata?.[ClerkMetadataKey.EMAIL_SIGNATURE];
  const accounts = isRecord(stored)
    ? parseSignatureAccounts(stored.accounts)
    : undefined;
  const topLevel = parseSignatureConfig(stored);
  if (!topLevel && !accounts) {
    return null;
  }
  return {
    ...(topLevel ?? DISABLED_EMAIL_SIGNATURE),
    ...(accounts ? { accounts } : {}),
  };
};

export const resolveEmailSignatureForAccount = (
  unsafeMetadata: Record<string, unknown> | null | undefined,
  address: string | null | undefined,
): EmailSignatureConfig => {
  const settings = readStoredEmailSignatureSettings(unsafeMetadata);
  const key = address ? normalizeSignatureAccountKey(address) : '';
  const accountConfig = key ? settings?.accounts?.[key] : undefined;
  if (accountConfig) {
    return accountConfig;
  }
  return settings
    ? { enabled: settings.enabled, html: settings.html }
    : DISABLED_EMAIL_SIGNATURE;
};

export const resolveSeedSignatureForAccount = (
  settings: EmailSignatureSettings | null,
  address: string | null | undefined,
  defaultHtml: string,
): EmailSignatureConfig => {
  const key = address ? normalizeSignatureAccountKey(address) : '';
  const accountConfig = key ? settings?.accounts?.[key] : undefined;
  if (accountConfig) {
    return accountConfig;
  }
  const hasTopLevel = Boolean(settings?.enabled || settings?.html);
  if (settings && hasTopLevel) {
    return { enabled: settings.enabled, html: settings.html };
  }
  return { enabled: false, html: defaultHtml };
};

export const getEmailSignatureSettingsSize = (
  settings: EmailSignatureSettings,
): number => new TextEncoder().encode(JSON.stringify(settings)).length;

export const isEmailSignatureSettingsOverBudget = (
  settings: EmailSignatureSettings,
): boolean =>
  getEmailSignatureSettingsSize(settings) > EMAIL_SIGNATURE_SETTINGS_MAX_BYTES;

const areConfigsEqual = (
  a: EmailSignatureConfig | undefined,
  b: EmailSignatureConfig | undefined,
): boolean => a?.enabled === b?.enabled && a?.html === b?.html;

export const areSignatureSettingsEqual = (
  a: EmailSignatureSettings,
  b: EmailSignatureSettings,
): boolean => {
  if (!areConfigsEqual(a, b)) {
    return false;
  }
  const aKeys = Object.keys(a.accounts ?? {});
  const bKeys = Object.keys(b.accounts ?? {});
  return (
    aKeys.length === bKeys.length &&
    aKeys.every((key) => areConfigsEqual(a.accounts?.[key], b.accounts?.[key]))
  );
};

export const isEmailSignatureOverLimit = (html: string): boolean =>
  html.length > EMAIL_SIGNATURE_MAX_HTML_LENGTH;

export const buildDefaultSignatureHtml = (
  fullName: string | null | undefined,
  title: string | null | undefined,
): string => {
  const name = fullName?.trim();
  const role = title?.trim();
  const nameLine = name
    ? `<p><strong>${escapeHtmlText(name)}</strong></p>`
    : '';
  const roleLine = role ? `<p>${escapeHtmlText(role)}</p>` : '';
  return `${nameLine}${roleLine}`;
};

export const readUserTitle = (
  unsafeMetadata: Record<string, unknown> | null | undefined,
): string | undefined => {
  const title = unsafeMetadata?.[ClerkMetadataKey.USER_TITLE];
  return typeof title === 'string' ? title : undefined;
};

export const buildSignatureBlock = (html: string): string => {
  const sanitized = sanitizeHtml(html);
  if (!sanitized) {
    return '';
  }

  const doc = new DOMParser().parseFromString(sanitized, 'text/html');
  doc.querySelectorAll('img').forEach((image) => image.remove());
  if (!doc.body.textContent?.trim()) {
    return '';
  }

  const block = doc.createElement('div');
  block.setAttribute(DATA_ATTRIBUTES.EMAIL_SIGNATURE, ATTRIBUTE_VALUES.TRUE);
  block.append(...Array.from(doc.body.childNodes));
  return block.outerHTML;
};

const insertAtEndOfUserInput = (
  userInput: string,
  addition: string,
): string => {
  if (!userInput.includes(DATA_ATTRIBUTES.USER_INPUT_AREA)) {
    return `${userInput}${addition}`;
  }

  const closingIndex = userInput.lastIndexOf(USER_INPUT_CLOSING_TAG);
  if (closingIndex === -1) {
    return `${userInput}${addition}`;
  }
  return `${userInput.slice(0, closingIndex)}${addition}${userInput.slice(closingIndex)}`;
};

export const applySignatureToBody = (
  body: string,
  signatureBlock: string,
): string => {
  if (!signatureBlock) {
    return body;
  }

  const { userInput, quotedContent } = splitEditorContent(body);
  if (hasSignatureBlock(userInput)) {
    return body;
  }

  if (!hasTemplateContent(body)) {
    const base = isContentEmpty(body) ? EMPTY_PARAGRAPH : body;
    return `${base}${signatureBlock}`;
  }

  const withSignature = insertAtEndOfUserInput(
    userInput,
    `${EMPTY_PARAGRAPH}${signatureBlock}`,
  );
  return `${withSignature}${quotedContent}`;
};

export const resolvePrefillMessage = (
  prefillBody: string,
  forwardedMessage: string | undefined,
  signatureBlock: string,
): string =>
  forwardedMessage
    ? replaceUserInputContent(forwardedMessage, prefillBody)
    : applySignatureToBody(convertPlainTextToHtml(prefillBody), signatureBlock);

export const resolveSignatureBlockForAccount = (
  unsafeMetadata: Record<string, unknown> | null | undefined,
  address: string | null | undefined,
): string => {
  const { enabled, html } = resolveEmailSignatureForAccount(
    unsafeMetadata,
    address,
  );
  return enabled ? buildSignatureBlock(html) : '';
};
